import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/auth/auth.service';
import { map, switchMap, tap } from 'rxjs/operators';

export interface CreateRequestPayload {
  idNumber?: string;
  streetAddress?: string;
  maxRecords: number;
}

export interface CreateTrustRequestPayload {
  trustNumber: string;
  maxRecords: number;
  requestPurpose?: string;
  consentRef?: string;
}

export interface DovsMatchPreview {
  consumerId?: string | null;
  initials?: string | null;
  firstName?: string | null;
  secondName?: string | null;
  surname?: string | null;
  idNo?: string | null;
  birthDate?: string | null;
  gender?: string | null;
  titleDesc?: string | null;
  cellularNo?: string | null;
  emailAddress?: string | null;
  residentialAddress?: string | null;
  cellNumberMatched?: number | null;
}

export interface DovsRequestListItem {
  requestId: string;
  coreRequestId: string | null;
  lookupDisplay: string | null;
  status: string;
  errorReason: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface CreateRequestResponse {
  requestId: string;
  status: string;
  message: string;
}

export interface CreateMultiRequestPayload {
  idNumbers: string[];
  maxRecords: number;
  source?: 'NEW_REQUEST' | 'ADDRESS_BOOK' | 'CSV_UPLOAD';
}

export interface CreateMultiTrustRequestPayload {
  trustNumbers: string[];
  maxRecords: number;
  source?: 'NEW_REQUEST' | 'CSV_UPLOAD';
}

export interface CreateMultiRequestResponse {
  requestBatchId: string;
  source: 'NEW_REQUEST' | 'ADDRESS_BOOK' | 'CSV_UPLOAD';
  totalInputs: number;
  submittedCount: number;
  failedCount: number;
  submittedRequestIds: string[];
  failed: Array<{ idNumber?: string; trustNumber?: string; reason: string; reasonCode?: string }>;
  message: string;
}

export interface FlagOutdatedResponse {
  mode: 'DIRECT_REQUEST' | 'PENDING_APPROVAL';
  requestId?: string;
  dataRequestId?: string;
  status: string;
  message: string;
}

export interface MarkDncResponse {
  requestId: string;
  status: 'DNC';
  message: string;
}

export interface RequestStatus {
  requestId: string;
  userId: string;
  idNumber?: string;
  streetAddress?: string;
  trustNumber?: string | null;
  lookupType?: 'ID' | 'ADDRESS' | 'TRUST' | 'DOVS' | null;
  lookupDisplay?: string | null;
  dncBlocked?: boolean;
  outdatedFlagPending?: boolean;
  status: 'IN_PROGRESS' | 'FAILED' | 'COMPLETE' | 'CANCELLED' | 'QUEUED';
  resultCode?: 'NO_MATCH' | null;
  errorReason?: string;
  errorReasonCode?: string | null;
  errorFamily?: 'UPSTREAM_PROVIDER' | 'REQUEST' | 'PLATFORM' | null;
  createdAt: any;
  updatedAt: any;
}

export interface RequestListItem {
  requestId: string;
  rawInput?: string | null;
  customerName?: string | null;
  idNumber?: string;
  streetAddress?: string;
  trustNumber?: string | null;
  lookupType?: 'ID' | 'ADDRESS' | 'TRUST' | 'DOVS' | null;
  lookupDisplay?: string | null;
  dncBlocked?: boolean;
  status: string;
  resultCode?: 'NO_MATCH' | null;
  createdAt: any;
  requestType?: 'SINGLE' | 'MULTI_SEARCH';
  multiSearch?: {
    requestBatchId: string;
    source: 'NEW_REQUEST' | 'ADDRESS_BOOK' | 'CSV_UPLOAD';
    total: number;
    complete: number;
    failed: number;
    inProgress: number;
    queued: number;
    cancelled: number;
  };
  viewAvailability?: {
    mode: 'FREE' | 'FREE_UNTIL' | 'CREDIT_COST' | 'NO_MATCH' | 'FAILED' | 'NOT_READY' | 'UNAVAILABLE' | 'INSUFFICIENT_CREDITS';
    freeUntil?: string | null;
    creditCost?: number;
  };
}

export interface RequestsListResponse {
  requests: RequestListItem[];
}

export interface MultiRequestListResponse {
  batch: {
    requestBatchId: string;
    source: 'NEW_REQUEST' | 'ADDRESS_BOOK' | 'CSV_UPLOAD';
    totalInputs: number;
    successfulInputs: number;
    failedInputs: number;
    createdAt: string | null;
    progress: {
      total: number;
      complete: number;
      failed: number;
      queued: number;
      inProgress: number;
      cancelled: number;
    };
    chargeSummary?: {
      completionChargeCount: number;
      completionChargeTotal: number;
    };
  };
  requests: RequestListItem[];
}

export interface AddressBookEntry {
  subjectKey: string;
  name: string | null;
  idNumber: string | null;
  latestCoreRequestId: string | null;
  latestRequestId: string | null;
  latestCompleteRequestId: string | null;
  latestStatus: string | null;
  resultCode?: 'NO_MATCH' | null;
  dncBlocked: boolean;
  lastLookedUpAt: string | null;
  requestType?: 'SINGLE' | 'MULTI_SEARCH';
  multiSearch?: {
    requestBatchId: string;
    source: 'NEW_REQUEST' | 'ADDRESS_BOOK' | 'CSV_UPLOAD';
    total: number;
    complete: number;
    failed: number;
    inProgress: number;
    queued: number;
    cancelled: number;
  };
  viewAvailability?: {
    mode: 'FREE' | 'FREE_UNTIL' | 'CREDIT_COST' | 'NO_MATCH' | 'FAILED' | 'NOT_READY' | 'UNAVAILABLE' | 'INSUFFICIENT_CREDITS';
    freeUntil?: string | null;
    creditCost?: number;
  };
}

export interface AddressBookResponse {
  entries: AddressBookEntry[];
}

export interface SubjectPropertyItem {
  id: string;
  propertyType?: string | null;
  erfNumber?: string | null;
  erfSize?: string | null;
  portionNumber?: string | null;
  townshipName?: string | null;
  areaName?: string | null;
  houseNumber?: string | null;
  streetName?: string | null;
  suburbName?: string | null;
  propertyNumber?: string | null;
  sg26Code?: string | null;
  summaryLine?: string | null;
  retrievedAt?: string | null;
  updatedAt?: string | null;
}

export interface TrustTrusteeCard {
  fullName?: string | null;
  surname?: string | null;
  idNumber?: string | null;
  age?: number | string | null;
  status?: string | null;
  riskBand?: string | null;
}

export interface TrustMatchCard {
  trustId?: string | null;
  trustNumber: string;
  name?: string | null;
  altTrustNumber?: string | null;
  mastersOffice?: string | null;
  registrationDate?: string | null;
  trustees: TrustTrusteeCard[];
}

export interface RequestDataResponse {
  data: Record<string, any>[];
  matches?: TrustMatchCard[];
  dovs?: Record<string, any> | null;
  lookupType?: string | null;
  resultCode?: 'NO_MATCH' | null;
  dataRetrievedAt?: string | null;
  cooldownFreeUntil?: string | null;
  profile?: {
    firstName?: string | null;
    surname?: string | null;
    idNumber?: string | null;
    birthDate?: string | null;
    gender?: string | null;
    title?: string | null;
    topTelephoneNumbers?: Array<{ number?: string; lastUpdatedDate?: string | null; confidencePct?: number | null }>;
    enquiryDate?: string | null;
    updatedAt?: any;
  } | null;
  noContactInfoNoCredit?: boolean;
  contactInfoMessage?: string | null;
}

export interface CreateDataRequestPayload {
  type: 'ADDRESS' | 'ID';
  address: string;
  idNumbers?: string[];
}

export interface CreateDataRequestResponse {
  dataRequestId: string;
  requestId?: string;
  status: string;
  message: string;
}

export interface DataRequestListItem {
  dataRequestId: string;
  userId: string;
  userEmail?: string | null; // User email (only available for admins)
  address: string;
  type?: 'ADDRESS' | 'ID';
  idNumbers?: string[];
  requestReason?: 'OUTDATED_FLAG' | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: any;
  updatedAt: any;
  requestId?: string;
}

export interface DataRequestsListResponse {
  dataRequests: DataRequestListItem[];
}

export interface ApproveDataRequestResponse {
  dataRequestId: string;
  requestId: string;
  status: string;
  message: string;
}

export interface RejectDataRequestResponse {
  dataRequestId: string;
  status: string;
  message: string;
}

export interface DeleteDataRequestResponse {
  dataRequestId: string;
  status: string;
  message: string;
}

// User Management interfaces
export interface UserListItem {
  userId: string;
  email: string | null;
  name?: string;
  surname?: string;
  companyName?: string | null;
  phone?: string | null;
  idNumber?: string | null;
  role: 'admin' | 'user';
  enabled: boolean;
  twoFactorEnabled?: boolean;
  twoFactorFailedAttempts?: number;
  billingSeatExempt?: boolean;
  creditAllocation?: number;
  creditsUsedThisPeriod?: number;
  invitePending?: boolean;
  team?: boolean;
  isBillingOwner?: boolean;
  approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  autoApproved?: boolean;
  industry?: 'REAL_ESTATE_PROFESSIONAL' | 'LEGAL_PROFESSIONAL' | 'OTHER' | null;
  industryOther?: string | null;
  activationPendingPayment?: boolean;
  selectedPlanDisplayName?: string | null;
  selectedPlanCode?: string | null;
  promoCampaignName?: string | null;
  promoCampaignCreatedAt?: string | null;
  idDocumentPath?: string | null;
  ffcDocumentPath?: string | null;
  creditsRemaining?: number;
  includedRemaining?: number;
  prepaidRemaining?: number;
}

export interface UsersListResponse {
  users: UserListItem[];
}

export interface CreateUserPayload {
  email: string;
  inviteToTeam?: boolean;
  role: 'admin' | 'user';
  isBillingOwner?: boolean;
  billingSeatExempt?: boolean;
  enableTrial?: boolean;
}

export interface CreateUserResponse {
  userId: string;
  message: string;
}

export interface UpdateUserPayload {
  name?: string;
  surname?: string;
  role?: 'admin' | 'user';
  enabled?: boolean;
  twoFactorEnabled?: boolean;
  billingSeatExempt?: boolean;
  enableTrial?: boolean;
  approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  team?: boolean;
  isBillingOwner?: boolean;
  autoApproveNewUsers?: boolean;
}

export interface UpdateUserResponse {
  message: string;
  requiresTwoFactorReauthentication?: boolean;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface ResendInviteResponse {
  message: string;
}

export interface PlatformOrganizationItem {
  orgId: string;
  displayName: string;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl: string | null;
  mailLogoUrl?: string | null;
  status: string;
  coreCompanyId: string;
  autoApproveNewUsers: boolean;
  billingPaymentExempt: boolean;
  billingPaymentType: 'CARD' | 'INVOICE';
  memberCount: number;
  totalSpendZarCents: number;
  currentMonthSpendZarCents: number;
  createdAt: string;
}

export interface PlatformOrganizationsResponse {
  organizations: PlatformOrganizationItem[];
}

export interface PlatformOrganizationBillingMonthItem {
  month: string;
  teamMembers: number;
  chargeCount: number;
  billedCents: number;
  settledCents: number;
  outstandingCents: number;
  firstChargeAt: string | null;
  lastChargeAt: string | null;
  dueAt: string | null;
  status: 'PAID' | 'PARTIAL' | 'OPEN' | 'OVERDUE';
}

export interface PlatformOrganizationBillingSummaryResponse {
  organization: {
    orgId: string;
    displayName: string;
    billingPaymentType: 'CARD' | 'INVOICE';
  };
  summary: {
    graceDays: number;
    teamMembers: number;
    totalBilledCents: number;
    totalSettledCents: number;
    totalDueCents: number;
    dueStatus: 'PAID' | 'PARTIAL' | 'OPEN' | 'OVERDUE';
  };
  months: PlatformOrganizationBillingMonthItem[];
}

export interface PlatformOrganizationInvoiceSettlePayload {
  amountCents: number;
  note?: string;
}

export interface PlatformOrganizationInvoiceSettleResponse {
  message: string;
  orgId: string;
  amountCents: number;
  reference: string;
}

export type FeatureFlagScope = 'org' | 'user';

export interface FeatureFlagRegistryEntry {
  key: string;
  label: string;
  scopes: FeatureFlagScope[];
  requiresRole: 'admin' | null;
}

export interface FeatureFlagRegistryResponse {
  flags: FeatureFlagRegistryEntry[];
}

export interface FeatureFlagOrgAssignment {
  organizationId: string;
  organizationName: string | null;
  enabled: boolean;
  updatedAt: string | Date;
  updatedByUserId: string | null;
}

export interface FeatureFlagUserAssignment {
  userId: string;
  email: string | null;
  name: string | null;
  surname: string | null;
  organizationId: string;
  organizationName: string | null;
  enabled: boolean;
  updatedAt: string | Date;
  updatedByUserId: string | null;
}

export interface FeatureFlagAssignmentsResponse {
  flagKey: string;
  organizations: FeatureFlagOrgAssignment[];
  users: FeatureFlagUserAssignment[];
}

export interface UpsertFeatureFlagPayload {
  flagKey: string;
  scope: FeatureFlagScope;
  targetId: string;
  enabled: boolean | null;
}

export interface UpsertFeatureFlagResponse {
  ok: boolean;
  flagKey: string;
  scope: FeatureFlagScope;
  targetId: string;
  enabled: boolean | null;
}

export interface OrganizationMembershipOption {
  membershipId: string;
  orgId: string;
  displayName: string;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl?: string | null;
  role: 'admin' | 'user';
  isBillingOwner: boolean;
  membershipStatus: string;
  orgStatus: string;
}

export interface OrganizationContextResponse {
  activeOrgId: string | null;
  organizations: OrganizationMembershipOption[];
}

export interface PlatformCompanyAdminItem {
  membershipId: string;
  userId: string;
  orgId: string;
  orgDisplayName: string;
  logoUrl?: string | null;
  email: string | null;
  name: string | null;
  surname: string | null;
  phone?: string | null;
  idNumber?: string | null;
  companyName?: string | null;
  role?: 'admin' | 'user';
  enabled?: boolean;
  twoFactorEnabled?: boolean;
  billingSeatExempt?: boolean;
  team?: boolean;
  isBillingOwner?: boolean;
  idDocumentPath?: string | null;
  selectedPlanDisplayName?: string | null;
  membershipStatus: string;
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  industry?: 'REAL_ESTATE_PROFESSIONAL' | 'LEGAL_PROFESSIONAL' | 'OTHER' | null;
  industryOther?: string | null;
  ffcDocumentPath?: string | null;
  idDocumentAvailable: boolean;
  invitePending: boolean;
  inviteCreatedAt: string;
}

export interface UserLegalDocumentUrlResponse {
  idDocumentPath: string | null;
  signedUrl: string | null;
  expiresAt: string | null;
  ffcDocumentPath?: string | null;
  ffcSignedUrl?: string | null;
  ffcExpiresAt?: string | null;
  industry?: 'REAL_ESTATE_PROFESSIONAL' | 'LEGAL_PROFESSIONAL' | 'OTHER' | null;
  industryOther?: string | null;
  phone?: string | null;
}

export interface PlatformCompanyAdminsResponse {
  companyAdmins: PlatformCompanyAdminItem[];
}

export interface DeletePlatformCompanyAdminResponse {
  message: string;
  membershipId: string;
  orgId: string;
  userId: string;
}

export interface CreatePlatformOrganizationPayload {
  displayName: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  mailLogoUrl?: string | null;
  initialAdminEmails: string[];
  billingOwnerEmail: string;
  billingPaymentType: 'CARD' | 'INVOICE';
}

export interface CreatePlatformOrganizationResponse {
  orgId: string;
  coreCompanyId: string;
  billingMode: 'TEAM';
  billingPaymentType: 'CARD' | 'INVOICE';
  invitedAdmins: string[];
  billingOwnerEmail: string;
  blockedRoleMismatchEmails: string[];
  message: string;
}

export interface UpdatePlatformOrganizationPayload {
  displayName: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  mailLogoUrl?: string | null;
  autoApproveNewUsers: boolean;
  billingPaymentExempt: boolean;
  billingPaymentType: 'CARD' | 'INVOICE';
  adminEmails?: string[];
  billingOwnerEmail?: string;
}

export interface UpdatePlatformOrganizationResponse {
  orgId: string;
  message: string;
  invitedAdmins?: string[];
  blockedRoleMismatchEmails?: string[];
}

export interface DeletePlatformOrganizationResponse {
  orgId: string;
  deletedUsers: number;
  cancelledSubscriptions: number;
  message: string;
}

export interface PlatformStatsDailyPoint {
  day: string;
  lookups: number;
}

export interface PlatformStatsFailureReason {
  reason: string;
  count: number;
}

export interface PlatformStatsOverviewResponse {
  summary: {
    lookupsToday: number;
    lookupsThisWeek: number;
    lookupsThisMonth: number;
    xdsRequestsThisMonth: number;
    servedFromCoreDbThisMonth: number;
    servedFromXdsThisMonth: number;
    completedThisMonth: number;
    failedThisMonth: number;
    pendingNow: number;
    successRate30d: number;
    avgTurnaroundMinutes30d: number;
    coreCacheHits30d: number;
    coreCacheMisses30d: number;
    activeTenantsThisWeek: number;
    activeCustomersThisWeek: number;
    creditsConsumedThisMonth: number;
  };
  trends: {
    lookupsDaily30d: PlatformStatsDailyPoint[];
    xdsRequestsDaily30d: PlatformStatsDailyPoint[];
  };
  failureReasons30d: PlatformStatsFailureReason[];
  generatedAt: string;
}

export interface PublicSystemStatusResponse {
  maintenanceModeActive: boolean;
  maintenanceMessage: string | null;
}

// Credits and usage interfaces
export interface CreditsSummaryResponse {
  monthlyCredits?: number;
  plans?: Array<{
    plan_code: string;
    display_name: string;
    plan_type: string;
    monthly_price_zar_cents: number;
    included_credits_per_user: number;
    topup_credit_price_cents: number;
  }>;
  note?: string;
}

export interface UserCreditsResponse {
  creditsRemaining: number;
  includedRemaining?: number;
  prepaidRemaining?: number;
  individualRemaining?: number;
  teamRemaining?: number;
  expiringSoonCredits?: number;
  nextIndividualExpiryAt?: string | null;
  nextIndividualExpiryCredits?: number;
  nextIndividualExpiryDays?: number | null;
  planDisplayName?: string | null;
  billingTermLabel?: string | null;
}

export interface UserInfoSummaryResponse {
  user: {
    userId: string;
    role: 'admin' | 'user';
    team?: boolean;
    approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  };
  credits: {
    creditsRemaining: number;
    includedRemaining?: number;
    prepaidRemaining?: number;
    individualRemaining?: number;
    teamRemaining?: number;
    expiringSoonCredits?: number;
    nextIndividualExpiryAt?: string | null;
    nextIndividualExpiryCredits?: number;
    nextIndividualExpiryDays?: number | null;
  };
  billing?: {
    planDisplayName: string | null;
    planCode: string | null;
    accountScope: 'individual' | 'team' | null;
    currentPeriodStartAt: string | null;
    currentPeriodEndAt: string | null;
  };
  pendingRequestsCount?: number;
  generatedAt: string;
}

export interface BillingProfileResponse {
  billingAccountId: string;
  accountScope: 'individual' | 'team';
  billingStatus: string;
  currentPlan: string;
  planCode?: string | null;
  nextPaymentDate: string | null;
  billingPaymentType?: 'CARD' | 'INVOICE';
  paymentMethod: {
    brand: string;
    masked: string | null;
    verified: boolean;
    recurringReusable?: boolean;
    recurringStatus?: string | null;
  } | null;
  billingPaymentExempt?: boolean;
  teamSeatCount?: number;
  teamUnitPriceCents?: number;
  teamTopupCreditPriceCents?: number;
  teamMonthlyTotalCents?: number;
  teamSeatBreakdown?: Array<{
    userId: string;
    displayName: string;
    email: string | null;
    role: 'admin' | 'user';
    isBillingOwner: boolean;
    monthlyPriceCents: number;
  }>;
  subscriptionPendingCancellation?: boolean;
  hasPaystackSubscription?: boolean;
  requiresRecurringSetup?: boolean;
  canCancelSubscription?: boolean;
  canReenableSubscription?: boolean;
  credits: {
    includedRemaining: number;
    prepaidRemaining: number;
    totalRemaining: number;
    expiringSoonCredits: number;
    nextIndividualExpiryAt?: string | null;
    nextIndividualExpiryDays?: number | null;
  };
}

export interface BillingUsageSummaryResponse {
  cycleStartAt: string;
  balances: {
    includedRemaining: number;
    prepaidRemaining: number;
    totalRemaining: number;
    expiringSoonCredits: number;
  };
  usage: {
    totalDebits: number;
    completionDebits: number;
    viewDebits: number;
  };
  estimation?: {
    estimatedCostPerLookup: number | null;
    maxRowsAllowed: number | null;
    source: 'core_company_summary' | 'unavailable';
  };
}

export interface BillingLedgerResponse {
  entries: Array<{
    id: string;
    entryType: string;
    amountCredits: number;
    direction: 'DEBIT' | 'CREDIT';
    bucketType: 'included' | 'prepaid' | null;
    chargeType: string | null;
    chargeReference: string | null;
    requestId: string | null;
    coreRequestId: string | null;
    createdAt: string;
  }>;
}

export interface RegisterPayload {
  name: string;
  surname: string;
  companyName?: string;
  email: string;
  phone: string;
  idNumber: string;
  industry: 'REAL_ESTATE_PROFESSIONAL' | 'LEGAL_PROFESSIONAL' | 'OTHER';
  industryOther?: string;
  password: string;
  idDocumentPath?: string;
  idDocumentBase64?: string;
  idDocumentMimeType?: string;
  idDocumentFileName?: string;
  ffcDocumentPath?: string;
  ffcDocumentBase64?: string;
  ffcDocumentMimeType?: string;
  ffcDocumentFileName?: string;
  signupTermsAccepted?: boolean;
  signupTermsVersion?: string;
  campaign?: string;
}

export interface PromoCampaignStats {
  redeemed: number;
  pending: number;
  approved: number;
  remaining: number;
}

export interface PromoCampaignListItem {
  promoCampaignId: string;
  name: string;
  publicToken: string;
  campaignUrl: string;
  status: 'ACTIVE' | 'PAUSED';
  maxUsers: number;
  expiresAt: string | null;
  createdByUserId?: string | null;
  createdAt: string;
  updatedAt?: string;
  stats: PromoCampaignStats;
}

export interface CreatePromoCampaignPayload {
  name: string;
  maxUsers?: number;
  expiresAt?: string | null;
}

export interface UpdatePromoCampaignPayload {
  name?: string;
  maxUsers?: number;
  expiresAt?: string | null;
  status?: 'ACTIVE' | 'PAUSED';
}

export interface PublicCampaignVerifyResponse {
  campaign: {
    name: string;
    orgDisplayName: string | null;
    expiresAt: string | null;
  };
}

export interface TenantSettingsResponse {
  autoApproveNewUsers: boolean;
}

export interface CurrentUserProfile {
  userId: string;
  email: string | null;
  name?: string | null;
  surname?: string | null;
  idNumber?: string | null;
  role: 'admin' | 'user' | null;
  isPlatformAdmin?: boolean;
  team?: boolean;
  isBillingOwner?: boolean;
  requiresTeamBillingSetup?: boolean;
  billingPaymentType?: 'CARD' | 'INVOICE';
  paymentMethodSetupRequired?: boolean;
  approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  selectedPlanId?: string | null;
  selectedPlanCode?: string | null;
  enabled: boolean;
  twoFactorEnabled?: boolean;
  features?: Record<string, boolean>;
  billingSeatExempt?: boolean;
}

export interface InitializeSubscriptionSetupResponse {
  reference: string;
  scope: 'individual' | 'team';
  planCode: string;
  seatCount: number;
  amountCents: number;
  monthlyAmountCents: number;
  periodKey?: string;
  authorizationUrl: string;
  accessCode: string;
  publicKey: string | null;
  setupBypassed?: boolean;
  pendingExisting?: boolean;
  alreadySettled?: boolean;
}

export interface SubscriptionSetupStatusResponse {
  reference: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  scope: 'individual' | 'team';
  planCode: string | null;
  subscriptionCode: string | null;
  message: string;
  updatedAt: string;
}

export interface TopupPackOption {
  packSize: number;
  unitPriceCents: number;
  totalAmountCents: number;
}

export interface BillingPlansResponse {
  scope: 'individual' | 'team';
  plans: Array<{
    planCode: string;
    displayName: string;
    planType: 'INDIVIDUAL' | 'TEAM';
    monthlyPriceCents: number;
    includedCreditsPerUser: number;
    topupCreditPriceCents: number;
  }>;
}

export interface CurrentUserProfileResponse {
  user: CurrentUserProfile;
}

export interface UpdateCurrentUserProfilePayload {
  name?: string;
  surname?: string;
  idNumber?: string;
}

export interface UpdateCurrentUserProfileResponse {
  message: string;
  user: {
    userId: string;
    email: string | null;
    name?: string | null;
    surname?: string | null;
    idNumber?: string | null;
  };
}

export interface CompleteSignupPayload {
  idNumber: string;
}

export interface PublicPasswordActionVerifyResponse {
  action: 'signup' | 'password_reset';
  email: string;
  name: string;
  surname: string;
  team?: boolean;
  companyName?: string;
  orgId?: string;
  expiresAt: number;
}

export interface PublicPasswordActionConsumePayload {
  token: string;
  password: string;
  action?: 'signup' | 'password_reset';
  name?: string;
  surname?: string;
  companyName?: string;
  phone?: string;
  idNumber?: string;
  industry?: 'REAL_ESTATE_PROFESSIONAL' | 'LEGAL_PROFESSIONAL' | 'OTHER';
  industryOther?: string;
  idDocumentPath?: string;
  idDocumentBase64?: string;
  idDocumentMimeType?: string;
  idDocumentFileName?: string;
  ffcDocumentPath?: string;
  ffcDocumentBase64?: string;
  ffcDocumentMimeType?: string;
  ffcDocumentFileName?: string;
  signupTermsAccepted?: boolean;
  signupTermsVersion?: string;
}

export interface PublicPasswordResetOtpRequestPayload {
  email: string;
}

export interface PublicPasswordResetOtpVerifyPayload {
  email: string;
  otp: string;
}

export interface AuditLogListEntry {
  id: string;
  action: string;
  timestamp: string | null;
  userEmail: string | null;
  source: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  summary: string;
  hasDetails: boolean;
}

export interface AuditLogsListResponse {
  entries: AuditLogListEntry[];
  nextCursor: string | null;
  pageSize: number;
}

export interface AuditLogDetailResponse {
  entry: Record<string, unknown>;
}

export interface SubmitConsentPayload {
  action: 'accept' | 'reject';
  selectedGrounds?: string[];
}

export interface SubmitDownloadConsentPayload {
  action: 'accept' | 'reject';
}

export interface ConsentStatusResponse {
  required: boolean;
  ttlMinutes: number;
  expiresAt: string | null;
}

export interface StreetGeometry {
  type: 'LineString' | 'MultiLineString';
  coordinates: number[][] | number[][][];
}

export interface StreetLineItem {
  streetKey: string;
  streetName: string;
  suburbName: string;
  roadType: string;
  pathCount: number;
  vertexCount: number;
  geojson: StreetGeometry;
}

export interface StreetLinesResponse {
  sourcePath: string;
  count: number;
  streets: StreetLineItem[];
}

export interface StreetHouseRow {
  subjectId: string;
  fullName: string | null;
  subjectIdNumber: string | null;
  houseNumber: string | null;
  erfNumber: string | null;
  summaryLine: string | null;
}

export interface StreetHousesResponse {
  streetName: string;
  suburbName: string;
  houseCount: number;
  rows: StreetHouseRow[];
}

export interface StreetSubjectProfileResponse {
  profile: {
    firstName?: string | null;
    surname?: string | null;
    idNumber?: string | null;
    birthDate?: string | null;
    gender?: string | null;
    title?: string | null;
    enquiryDate?: string | null;
    updatedAt?: string | null;
    topTelephoneNumbers?: Array<{ number?: string; lastUpdatedDate?: string | null; confidencePct?: number | null }>;
  } | null;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl;
  private userInfoSummaryRefreshSubject = new Subject<void>();
  private readonly activeOrgStorageKey = 'activeOrgId';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  get userInfoSummaryRefreshRequested$(): Observable<void> {
    return this.userInfoSummaryRefreshSubject.asObservable();
  }

  requestUserInfoSummaryRefresh(): void {
    this.userInfoSummaryRefreshSubject.next();
  }

  private async getHeadersAsync(): Promise<HttpHeaders> {
    const token = await this.authService.getIdToken();
    const twoFactorSession = this.authService.getTwoFactorSessionToken();
    const actAsUserId = this.authService.getActAsUserId();
    const activeOrgId = this.getActiveOrgId();
    const headers: any = {
      'Content-Type': 'application/json'
    };
    
    // Only add Authorization header if we have a token
    // In emulator mode, auth is bypassed, but we still try to send token if available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (twoFactorSession) {
      headers['X-2FA-Session'] = twoFactorSession;
    }
    if (actAsUserId) {
      headers['X-Act-As-User'] = actAsUserId;
    }
    if (activeOrgId) {
      headers['X-Org-Id'] = activeOrgId;
    }
    
    return new HttpHeaders(headers);
  }

  getActiveOrgId(): string | null {
    try {
      const value = localStorage.getItem(this.activeOrgStorageKey);
      return value && value.trim() ? value.trim() : null;
    } catch {
      return null;
    }
  }

  setActiveOrgId(orgId: string | null): void {
    try {
      if (!orgId) {
        localStorage.removeItem(this.activeOrgStorageKey);
        return;
      }
      localStorage.setItem(this.activeOrgStorageKey, orgId);
    } catch {
      // Ignore storage access failures.
    }
  }

  private getHeaders(): Observable<HttpHeaders> {
    return new Observable(observer => {
      this.getHeadersAsync().then(headers => {
        observer.next(headers);
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  createRequest(payload: CreateRequestPayload): Observable<CreateRequestResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<CreateRequestResponse>(
          `${this.apiUrl}/requests`,
          payload,
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  createTrustRequest(payload: CreateTrustRequestPayload): Observable<CreateRequestResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<CreateRequestResponse>(
          `${this.apiUrl}/requests/trust`,
          payload,
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  dovsMatch(payload: {
    idNumber: string;
    cellNumber: string;
    yourReference?: string;
    voucherCode?: string;
    requestPurpose?: string;
    consentRef?: string;
  }): Observable<{
    matchSessionId: string | null;
    preview: DovsMatchPreview | null;
    matchExpiresAt: string | null;
    resultCode?: 'NO_MATCH';
  }> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.post<{
          matchSessionId: string | null;
          preview: DovsMatchPreview | null;
          matchExpiresAt: string | null;
          resultCode?: 'NO_MATCH';
        }>(`${this.apiUrl}/requests/dovs/match`, payload, { headers })
      )
    );
  }

  dovsStart(payload: {
    matchSessionId: string;
    idNumber: string;
    cellNumber: string;
    requestPurpose?: string;
    consentRef?: string;
    redirectUrl?: string;
  }): Observable<{
    requestId: string;
    coreRequestId: string;
    status: string;
    referenceNo: string | null;
    cellNumberMasked: string;
    lookupDisplay: string;
    message: string;
  }> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.post<{
          requestId: string;
          coreRequestId: string;
          status: string;
          referenceNo: string | null;
          cellNumberMasked: string;
          lookupDisplay: string;
          message: string;
        }>(`${this.apiUrl}/requests/dovs/start`, payload, { headers })
      ),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  listDovsRequests(limit = 50): Observable<{ requests: DovsRequestListItem[] }> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.get<{ requests: DovsRequestListItem[] }>(`${this.apiUrl}/requests/dovs`, {
          headers,
          params: { limit: String(limit) },
        })
      )
    );
  }

  refreshDovsRequest(requestId: string): Observable<{
    requestId: string;
    status: string;
    completed: boolean;
    expired: boolean;
  }> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.post<{
          requestId: string;
          status: string;
          completed: boolean;
          expired: boolean;
        }>(`${this.apiUrl}/requests/${requestId}/dovs/refresh`, {}, { headers })
      ),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  createMultiRequest(payload: CreateMultiRequestPayload): Observable<CreateMultiRequestResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<CreateMultiRequestResponse>(
          `${this.apiUrl}/requests/multi`,
          payload,
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  createTrustMultiRequest(payload: CreateMultiTrustRequestPayload): Observable<CreateMultiRequestResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<CreateMultiRequestResponse>(
          `${this.apiUrl}/requests/trust/multi`,
          payload,
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  getRequestStatus(requestId: string): Observable<RequestStatus> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<RequestStatus>(
          `${this.apiUrl}/requests/${requestId}`,
          { headers }
        );
      })
    );
  }

  listRequests(status?: string): Observable<RequestsListResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        let url = `${this.apiUrl}/requests`;
        if (status) url += `?status=${status}`;
        return this.http.get<RequestsListResponse>(url, { headers });
      })
    );
  }

  listMultiRequestItems(requestBatchId: string): Observable<MultiRequestListResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<MultiRequestListResponse>(
          `${this.apiUrl}/requests/multi/${encodeURIComponent(requestBatchId)}`,
          { headers }
        );
      })
    );
  }

  listAddressBook(search?: string): Observable<AddressBookResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        let url = `${this.apiUrl}/requests/address-book`;
        const trimmed = (search || '').trim();
        if (trimmed) {
          url += `?search=${encodeURIComponent(trimmed)}`;
        }
        return this.http.get<AddressBookResponse>(url, { headers });
      })
    );
  }

  getRequestData(requestId: string): Observable<RequestDataResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<RequestDataResponse>(
          `${this.apiUrl}/requests/${requestId}/view`,
          {},
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  cancelRequest(requestId: string): Observable<{ requestId: string; status: string; message: string }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<{ requestId: string; status: string; message: string }>(
          `${this.apiUrl}/requests/${requestId}/cancel`,
          {},
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  flagRequestOutdated(requestId: string): Observable<FlagOutdatedResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<FlagOutdatedResponse>(
          `${this.apiUrl}/requests/${requestId}/flag-outdated`,
          {},
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  markRequestDnc(requestId: string): Observable<MarkDncResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<MarkDncResponse>(
          `${this.apiUrl}/requests/${requestId}/mark-dnc`,
          {},
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  // Data Request endpoints
  createDataRequest(payload: CreateDataRequestPayload): Observable<CreateDataRequestResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<CreateDataRequestResponse>(
          `${this.apiUrl}/data-requests`,
          payload,
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  listDataRequests(status?: string): Observable<DataRequestsListResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        let url = `${this.apiUrl}/data-requests`;
        if (status) url += `?status=${status}`;
        return this.http.get<DataRequestsListResponse>(url, { headers });
      })
    );
  }

  approveDataRequest(dataRequestId: string): Observable<ApproveDataRequestResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<ApproveDataRequestResponse>(
          `${this.apiUrl}/data-requests/${dataRequestId}/approve`,
          {},
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  rejectDataRequest(dataRequestId: string): Observable<RejectDataRequestResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<RejectDataRequestResponse>(
          `${this.apiUrl}/data-requests/${dataRequestId}/reject`,
          {},
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  deleteDataRequest(dataRequestId: string): Observable<DeleteDataRequestResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.delete<DeleteDataRequestResponse>(
          `${this.apiUrl}/data-requests/${dataRequestId}`,
          { headers }
        );
      }),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  // User Management endpoints
  listUsers(): Observable<UsersListResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<UsersListResponse>(
          `${this.apiUrl}/users`,
          { headers }
        );
      })
    );
  }

  createUser(payload: CreateUserPayload): Observable<CreateUserResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<CreateUserResponse>(
          `${this.apiUrl}/users`,
          payload,
          { headers }
        );
      })
    );
  }

  updateUser(userId: string, payload: UpdateUserPayload, orgId?: string | null): Observable<UpdateUserResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        const query = orgId && orgId.trim()
          ? `?orgId=${encodeURIComponent(orgId.trim())}`
          : '';
        return this.http.put<UpdateUserResponse>(
          `${this.apiUrl}/users/${userId}${query}`,
          payload,
          { headers }
        );
      })
    );
  }

  getUserIdDocumentUrl(
    userId: string,
    orgId?: string | null
  ): Observable<UserLegalDocumentUrlResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        const query = orgId && orgId.trim()
          ? `?orgId=${encodeURIComponent(orgId.trim())}`
          : '';
        return this.http.get<UserLegalDocumentUrlResponse>(
          `${this.apiUrl}/users/${encodeURIComponent(userId)}/id-document-url${query}`,
          { headers }
        );
      })
    );
  }

  deleteUser(userId: string): Observable<{ message: string }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.delete<{ message: string }>(
          `${this.apiUrl}/users/${userId}`,
          { headers }
        );
      })
    );
  }

  resetUserPassword(userId: string, orgId?: string | null): Observable<ResetPasswordResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        const query = orgId && orgId.trim()
          ? `?orgId=${encodeURIComponent(orgId.trim())}`
          : '';
        return this.http.post<ResetPasswordResponse>(
          `${this.apiUrl}/users/${encodeURIComponent(userId)}/reset-password${query}`,
          {},
          { headers }
        );
      })
    );
  }

  resendUserInvite(userId: string): Observable<ResendInviteResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<ResendInviteResponse>(
          `${this.apiUrl}/users/${userId}/resend-invite`,
          {},
          { headers }
        );
      })
    );
  }

  listPlatformOrganizations(): Observable<PlatformOrganizationsResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<PlatformOrganizationsResponse>(`${this.apiUrl}/platform/organizations`, { headers })
      )
    );
  }

  listMyOrganizations(): Observable<OrganizationContextResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<OrganizationContextResponse>(`${this.apiUrl}/orgs`, { headers })
      ),
      tap((response) => {
        if (response?.activeOrgId) {
          this.setActiveOrgId(response.activeOrgId);
        }
      })
    );
  }

  selectMyOrganization(orgId: string): Observable<{ message: string; orgId: string }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{ message: string; orgId: string }>(
          `${this.apiUrl}/orgs/select`,
          { orgId },
          { headers }
        )
      ),
      tap((response) => {
        if (response?.orgId) {
          this.setActiveOrgId(response.orgId);
        }
      })
    );
  }

  listPlatformCompanyAdmins(): Observable<PlatformCompanyAdminsResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<PlatformCompanyAdminsResponse>(`${this.apiUrl}/platform/company-admins`, { headers })
      )
    );
  }

  approvePlatformCompanyAdmin(membershipId: string): Observable<{ message: string; membershipId: string; orgId: string }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{ message: string; membershipId: string; orgId: string }>(
          `${this.apiUrl}/platform/company-admins/${encodeURIComponent(membershipId)}/approve`,
          {},
          { headers }
        )
      )
    );
  }

  rejectPlatformCompanyAdmin(membershipId: string): Observable<{ message: string; membershipId: string; orgId: string }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{ message: string; membershipId: string; orgId: string }>(
          `${this.apiUrl}/platform/company-admins/${encodeURIComponent(membershipId)}/reject`,
          {},
          { headers }
        )
      )
    );
  }

  resendPlatformCompanyAdminInvite(membershipId: string): Observable<{ message: string; membershipId: string }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{ message: string; membershipId: string }>(
          `${this.apiUrl}/platform/company-admins/${encodeURIComponent(membershipId)}/resend-invite`,
          {},
          { headers }
        )
      )
    );
  }

  deletePlatformCompanyAdmin(membershipId: string): Observable<DeletePlatformCompanyAdminResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<DeletePlatformCompanyAdminResponse>(
          `${this.apiUrl}/platform/company-admins/${encodeURIComponent(membershipId)}/delete`,
          {},
          { headers }
        )
      )
    );
  }

  createPlatformOrganization(payload: CreatePlatformOrganizationPayload): Observable<CreatePlatformOrganizationResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<CreatePlatformOrganizationResponse>(`${this.apiUrl}/platform/organizations`, payload, { headers })
      )
    );
  }

  updatePlatformOrganization(
    orgId: string,
    payload: UpdatePlatformOrganizationPayload
  ): Observable<UpdatePlatformOrganizationResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.put<UpdatePlatformOrganizationResponse>(
          `${this.apiUrl}/platform/organizations/${encodeURIComponent(orgId)}`,
          payload,
          { headers }
        )
      )
    );
  }

  deletePlatformOrganization(orgId: string): Observable<DeletePlatformOrganizationResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.delete<DeletePlatformOrganizationResponse>(
          `${this.apiUrl}/platform/organizations/${encodeURIComponent(orgId)}`,
          { headers }
        )
      )
    );
  }

  listPlatformOrganizationUsers(orgId: string): Observable<UsersListResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<UsersListResponse>(
          `${this.apiUrl}/platform/organizations/${encodeURIComponent(orgId)}/users`,
          { headers }
        )
      )
    );
  }

  getPlatformOrganizationBillingSummary(orgId: string): Observable<PlatformOrganizationBillingSummaryResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<PlatformOrganizationBillingSummaryResponse>(
          `${this.apiUrl}/platform/organizations/${encodeURIComponent(orgId)}/billing`,
          { headers }
        )
      )
    );
  }

  settlePlatformOrganizationInvoice(
    orgId: string,
    payload: PlatformOrganizationInvoiceSettlePayload
  ): Observable<PlatformOrganizationInvoiceSettleResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<PlatformOrganizationInvoiceSettleResponse>(
          `${this.apiUrl}/platform/organizations/${encodeURIComponent(orgId)}/billing/settle`,
          payload,
          { headers }
        )
      )
    );
  }

  getPlatformStatsOverview(): Observable<PlatformStatsOverviewResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<PlatformStatsOverviewResponse>(`${this.apiUrl}/platform/stats/overview`, { headers })
      )
    );
  }

  listFeatureFlagRegistry(): Observable<FeatureFlagRegistryResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<FeatureFlagRegistryResponse>(`${this.apiUrl}/platform/feature-flags/registry`, { headers })
      )
    );
  }

  listFeatureFlagAssignments(
    flagKey: string,
    opts?: { orgId?: string }
  ): Observable<FeatureFlagAssignmentsResponse> {
    const params = new URLSearchParams();
    params.set('flagKey', flagKey);
    const orgId = opts?.orgId?.trim();
    if (orgId) {
      params.set('orgId', orgId);
    }
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<FeatureFlagAssignmentsResponse>(
          `${this.apiUrl}/platform/feature-flags/assignments?${params.toString()}`,
          { headers }
        )
      )
    );
  }

  upsertFeatureFlagAssignment(
    payload: UpsertFeatureFlagPayload
  ): Observable<UpsertFeatureFlagResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.put<UpsertFeatureFlagResponse>(`${this.apiUrl}/platform/feature-flags`, payload, { headers })
      )
    );
  }

  getPublicSystemStatus(): Observable<PublicSystemStatusResponse> {
    return this.http.get<PublicSystemStatusResponse>(`${this.apiUrl}/public/system-status`);
  }

  // Credits endpoints
  getCreditsSummary(): Observable<CreditsSummaryResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<CreditsSummaryResponse>(
          `${this.apiUrl}/credits/summary`,
          { headers }
        );
      })
    );
  }

  getUserInfoSummary(): Observable<UserInfoSummaryResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<UserInfoSummaryResponse>(
          `${this.apiUrl}/user-info-summary`,
          { headers }
        );
      })
    );
  }

  getCurrentUserProfile(): Observable<CurrentUserProfileResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<CurrentUserProfileResponse>(
          `${this.apiUrl}/me`,
          { headers }
        );
      })
    );
  }

  updateCurrentUserProfile(payload: UpdateCurrentUserProfilePayload): Observable<UpdateCurrentUserProfileResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.put<UpdateCurrentUserProfileResponse>(
          `${this.apiUrl}/me`,
          payload,
          { headers }
        );
      })
    );
  }

  startTwoFactorChallenge(): Observable<{
    required: boolean;
    challengeId?: string;
    expiresAt?: string;
    resendAvailableAt?: string;
  }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<{
          required: boolean;
          challengeId?: string;
          expiresAt?: string;
          resendAvailableAt?: string;
        }>(
          `${this.apiUrl}/auth/2fa/start`,
          {},
          { headers }
        );
      })
    );
  }

  resendTwoFactorChallenge(): Observable<{
    message: string;
    challengeId: string;
    expiresAt: string;
    resendAvailableAt: string;
  }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<{
          message: string;
          challengeId: string;
          expiresAt: string;
          resendAvailableAt: string;
        }>(
          `${this.apiUrl}/auth/2fa/resend`,
          {},
          { headers }
        );
      })
    );
  }

  verifyTwoFactorChallenge(payload: {
    challengeId: string;
    otp: string;
  }): Observable<{
    message: string;
    sessionToken: string;
    sessionExpiresAt: string;
  }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<{
          message: string;
          sessionToken: string;
          sessionExpiresAt: string;
        }>(
          `${this.apiUrl}/auth/2fa/verify`,
          payload,
          { headers }
        );
      })
    );
  }

  completeSignup(payload: CompleteSignupPayload): Observable<{ message: string }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<{ message: string }>(
          `${this.apiUrl}/me/complete-signup`,
          payload,
          { headers }
        );
      })
    );
  }

  verifyPasswordActionToken(token: string): Observable<PublicPasswordActionVerifyResponse> {
    return this.http.get<PublicPasswordActionVerifyResponse>(
      `${this.apiUrl}/auth/password-action/verify?token=${encodeURIComponent(token)}`
    );
  }

  consumePasswordActionToken(
    payload: PublicPasswordActionConsumePayload
  ): Observable<{ message: string; approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' }> {
    return this.http.post<{ message: string; approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' }>(
      `${this.apiUrl}/auth/password-action/consume`,
      payload,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      }
    );
  }

  requestPasswordResetOtp(payload: PublicPasswordResetOtpRequestPayload): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/auth/password-action/request-reset-otp`,
      payload,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      }
    );
  }

  verifyPasswordResetOtp(payload: PublicPasswordResetOtpVerifyPayload): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/auth/password-action/verify-reset-otp`,
      payload,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      }
    );
  }

  getBillingProfile(): Observable<BillingProfileResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<BillingProfileResponse>(`${this.apiUrl}/billing/profile`, { headers })
      )
    );
  }

  getBillingPlans(scope: 'individual' | 'team' = 'individual'): Observable<BillingPlansResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<{
          scope: 'individual' | 'team';
          plans: Array<{
            plan_code: string;
            display_name: string;
            plan_type: 'INDIVIDUAL' | 'TEAM';
            monthly_price_zar_cents: number;
            included_credits_per_user: number;
            topup_credit_price_cents: number;
          }>;
        }>(`${this.apiUrl}/billing/plans?scope=${encodeURIComponent(scope)}`, { headers })
      ),
      map((response): BillingPlansResponse => ({
        scope: response.scope,
        plans: (response.plans || []).map((plan) => ({
          planCode: plan.plan_code,
          displayName: plan.display_name,
          planType: plan.plan_type,
          monthlyPriceCents: Number(plan.monthly_price_zar_cents || 0),
          includedCreditsPerUser: Number(plan.included_credits_per_user || 0),
          topupCreditPriceCents: Number(plan.topup_credit_price_cents || 0),
        })),
      }))
    );
  }

  getBillingUsageSummary(): Observable<BillingUsageSummaryResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<BillingUsageSummaryResponse>(`${this.apiUrl}/billing/usage-summary`, { headers })
      )
    );
  }

  getBillingLedger(limit = 100): Observable<BillingLedgerResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<BillingLedgerResponse>(`${this.apiUrl}/billing/ledger?limit=${encodeURIComponent(String(limit))}`, {
          headers,
        })
      )
    );
  }

  getTopupPacks(): Observable<{ packs: TopupPackOption[]; teamPricingApplied: boolean }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<{ packs: TopupPackOption[]; teamPricingApplied: boolean }>(`${this.apiUrl}/billing/topups/packs`, {
          headers,
        })
      )
    );
  }

  purchaseTopup(
    packSize: number,
    options?: { returnPath?: '/billing/plan-selector' | '/my-account' }
  ): Observable<{
    reference: string;
    packSize: number;
    unitPriceCents: number;
    totalAmountCents: number;
    expiresAt: string;
    status: 'PENDING';
    authorizationUrl: string;
    accessCode: string;
    publicKey: string | null;
  }> {
    const body: { packSize: number; returnPath?: string } = { packSize };
    if (options?.returnPath) {
      body.returnPath = options.returnPath;
    }
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{
          reference: string;
          packSize: number;
          unitPriceCents: number;
          totalAmountCents: number;
          expiresAt: string;
          status: 'PENDING';
          authorizationUrl: string;
          accessCode: string;
          publicKey: string | null;
        }>(`${this.apiUrl}/billing/topups/purchase`, body, { headers })
      ),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  selectBillingPlan(planCode: string): Observable<{
    message: string;
    plan: {
      planCode: string;
      displayName: string;
      monthlyPriceCents: number;
      includedCreditsPerUser: number;
    };
    billingAccountId: string;
  }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{
          message: string;
          plan: {
            planCode: string;
            displayName: string;
            monthlyPriceCents: number;
            includedCreditsPerUser: number;
          };
          billingAccountId: string;
        }>(`${this.apiUrl}/billing/plan/select`, { planCode }, { headers })
      ),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  verifyTopupPurchase(reference: string): Observable<{
    message: string;
    reference: string;
    packSize: number;
    unitPriceCents: number;
    totalAmountCents: number;
    expiresAt: string;
  }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{
          message: string;
          reference: string;
          packSize: number;
          unitPriceCents: number;
          totalAmountCents: number;
          expiresAt: string;
        }>(`${this.apiUrl}/billing/topups/verify`, { reference }, { headers })
      ),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  getTopupPurchaseStatus(reference: string): Observable<{
    reference: string;
    status: 'PENDING' | 'SUCCESS' | 'FAILED';
    message: string;
    updatedAt: string;
  }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<{
          reference: string;
          status: 'PENDING' | 'SUCCESS' | 'FAILED';
          message: string;
          updatedAt: string;
        }>(`${this.apiUrl}/billing/topups/status?reference=${encodeURIComponent(reference)}`, { headers })
      )
    );
  }

  assignPrepaid(payload: { purchaseId: string; assignedToUserId: string; assignedCredits: number }): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post(`${this.apiUrl}/billing/prepaid/assign`, payload, { headers })
      ),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  changeBillingPlan(planCode: string): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post(`${this.apiUrl}/billing/plan/change`, { planCode }, { headers })
      ),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  cancelSubscription(): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers => this.http.post(`${this.apiUrl}/billing/subscription/cancel`, {}, { headers }))
    );
  }

  reenableSubscription(): Observable<{ message: string; subscriptionCode: string }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{ message: string; subscriptionCode: string }>(
          `${this.apiUrl}/billing/subscription/re-enable`,
          {},
          { headers }
        )
      )
    );
  }

  initPaymentMethod(
    provider: 'paystack' = 'paystack',
    options: { seatActivationPaymentReference?: string } = {}
  ): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{
          provider: 'paystack';
          setupReference: string;
          amountCents: number;
          authorizationUrl: string;
          accessCode: string;
          publicKey: string | null;
          message: string;
        }>(
          `${this.apiUrl}/billing/payment-method/init`,
          {
            provider,
            seatActivationPaymentReference: options.seatActivationPaymentReference || undefined,
          },
          { headers }
        )
      )
    );
  }

  getPaymentMethodSetupStatus(reference: string): Observable<{
    reference: string;
    status: 'PENDING' | 'SUCCESS' | 'FAILED';
    message: string;
    updatedAt: string;
  }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<{
          reference: string;
          status: 'PENDING' | 'SUCCESS' | 'FAILED';
          message: string;
          updatedAt: string;
        }>(`${this.apiUrl}/billing/payment-method/status?reference=${encodeURIComponent(reference)}`, { headers })
      )
    );
  }

  verifyPaymentMethod(payload: {
    authorizationCode: string;
    cardBrand: string;
    cardLast4: string;
    expMonth?: number;
    expYear?: number;
    seatActivationPaymentReference?: string;
  }): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post(`${this.apiUrl}/billing/payment-method/verify`, payload, { headers })
      )
    );
  }

  getPaymentMethod(): Observable<any> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get(`${this.apiUrl}/billing/payment-method`, { headers })
      )
    );
  }

  getSubscriptionManageLink(): Observable<{ url: string; subscriptionCode: string }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<{ url: string; subscriptionCode: string }>(`${this.apiUrl}/billing/subscription/manage-link`, {
          headers,
        })
      )
    );
  }

  initializeSubscriptionSetup(payload: {
    planCode?: string;
    scope?: 'individual' | 'team';
  }): Observable<InitializeSubscriptionSetupResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<InitializeSubscriptionSetupResponse>(`${this.apiUrl}/billing/subscription/init`, payload, { headers })
      )
    );
  }

  getSubscriptionSetupStatus(reference: string): Observable<SubscriptionSetupStatusResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<SubscriptionSetupStatusResponse>(
          `${this.apiUrl}/billing/subscription/status?reference=${encodeURIComponent(reference)}`,
          { headers }
        )
      )
    );
  }

  verifySubscriptionSetup(reference: string): Observable<{
    message: string;
    reference: string;
    subscriptionCode: string | null;
    planCode: string;
    scope: 'individual' | 'team';
  }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.post<{
          message: string;
          reference: string;
          subscriptionCode: string | null;
          planCode: string;
          scope: 'individual' | 'team';
        }>(`${this.apiUrl}/billing/subscription/verify`, { reference }, { headers })
      ),
      tap(() => this.requestUserInfoSummaryRefresh())
    );
  }

  getTenantSettings(): Observable<TenantSettingsResponse> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.get<TenantSettingsResponse>(`${this.apiUrl}/tenant-settings`, { headers })
      )
    );
  }

  updateTenantSettings(payload: TenantSettingsResponse): Observable<{ message: string; autoApproveNewUsers: boolean }> {
    return this.getHeaders().pipe(
      switchMap(headers =>
        this.http.put<{ message: string; autoApproveNewUsers: boolean }>(`${this.apiUrl}/tenant-settings`, payload, { headers })
      )
    );
  }

  register(payload: RegisterPayload): Observable<{ userId: string; approvalStatus: string; message: string }> {
    return this.http.post<{ userId: string; approvalStatus: string; message: string }>(
      `${this.apiUrl}/register`,
      payload,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      }
    );
  }

  verifyPublicCampaign(token: string): Observable<PublicCampaignVerifyResponse> {
    return this.http.get<PublicCampaignVerifyResponse>(
      `${this.apiUrl}/public/campaigns/verify?campaign=${encodeURIComponent(token)}`
    );
  }

  listCampaigns(): Observable<{ campaigns: PromoCampaignListItem[] }> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.get<{ campaigns: PromoCampaignListItem[] }>(`${this.apiUrl}/campaigns`, { headers })
      )
    );
  }

  createCampaign(payload: CreatePromoCampaignPayload): Observable<{ campaign: PromoCampaignListItem }> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.post<{ campaign: PromoCampaignListItem }>(`${this.apiUrl}/campaigns`, payload, { headers })
      )
    );
  }

  updateCampaign(
    promoCampaignId: string,
    payload: UpdatePromoCampaignPayload
  ): Observable<{ campaign: PromoCampaignListItem }> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.put<{ campaign: PromoCampaignListItem }>(
          `${this.apiUrl}/campaigns/${encodeURIComponent(promoCampaignId)}`,
          payload,
          { headers }
        )
      )
    );
  }

  deleteCampaign(
    promoCampaignId: string
  ): Observable<{ message: string; promoCampaignId: string }> {
    return this.getHeaders().pipe(
      switchMap((headers) =>
        this.http.delete<{ message: string; promoCampaignId: string }>(
          `${this.apiUrl}/campaigns/${encodeURIComponent(promoCampaignId)}`,
          { headers }
        )
      )
    );
  }

  submitConsent(payload: SubmitConsentPayload): Observable<{ message: string }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<{ message: string }>(
          `${this.apiUrl}/consent`,
          payload,
          { headers }
        );
      })
    );
  }

  submitRequestDownloadConsent(
    requestId: string,
    payload: SubmitDownloadConsentPayload
  ): Observable<{ message: string; consentEventId: string }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<{ message: string; consentEventId: string }>(
          `${this.apiUrl}/requests/${encodeURIComponent(requestId)}/download-consent`,
          payload,
          { headers }
        );
      })
    );
  }

  submitBatchDownloadConsent(
    requestBatchId: string,
    payload: SubmitDownloadConsentPayload
  ): Observable<{ message: string; requestBatchId: string; recordsUpdated: number }> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<{ message: string; requestBatchId: string; recordsUpdated: number }>(
          `${this.apiUrl}/requests/multi/${encodeURIComponent(requestBatchId)}/download-consent`,
          payload,
          { headers }
        );
      })
    );
  }

  getConsentStatus(): Observable<ConsentStatusResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<ConsentStatusResponse>(
          `${this.apiUrl}/consent/status`,
          { headers }
        );
      })
    );
  }

  listUserAuditLogs(
    userId: string,
    options?: { limit?: number; cursor?: string | null }
  ): Observable<AuditLogsListResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        const params = new URLSearchParams();
        if (options?.limit != null) {
          params.set('limit', String(options.limit));
        }
        if (options?.cursor) {
          params.set('cursor', options.cursor);
        }
        const q = params.toString();
        const url = `${this.apiUrl}/users/${encodeURIComponent(userId)}/audit-logs${q ? `?${q}` : ''}`;
        return this.http.get<AuditLogsListResponse>(url, { headers });
      })
    );
  }

  getUserAuditLog(userId: string, logId: string): Observable<AuditLogDetailResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<AuditLogDetailResponse>(
          `${this.apiUrl}/users/${encodeURIComponent(userId)}/audit-logs/${encodeURIComponent(logId)}`,
          { headers }
        );
      })
    );
  }

  listStreetLines(streetName?: string, suburbName?: string): Observable<StreetLinesResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        const params = new URLSearchParams();
        if (streetName) params.set('streetName', streetName);
        if (suburbName) params.set('suburbName', suburbName);
        const q = params.toString();
        const url = `${this.apiUrl}/street-search/streets${q ? `?${q}` : ''}`;
        return this.http.get<StreetLinesResponse>(url, { headers });
      })
    );
  }

  listStreetHouses(streetName: string, suburbName: string): Observable<StreetHousesResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        const params = new URLSearchParams();
        params.set('streetName', streetName);
        params.set('suburbName', suburbName);
        const url = `${this.apiUrl}/street-search/houses?${params.toString()}`;
        return this.http.get<StreetHousesResponse>(url, { headers });
      })
    );
  }

  getStreetSubjectProfile(subjectId: string): Observable<StreetSubjectProfileResponse> {
    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.get<StreetSubjectProfileResponse>(
          `${this.apiUrl}/street-search/subjects/${encodeURIComponent(subjectId)}`,
          { headers }
        );
      })
    );
  }
}
